import WooMoments from '@efex-components/common/Modal/WooMoments';
import { Dialog, DialogContent, DialogTitle } from '@efexteam/components';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import { Skeleton, Typography } from '@mui/material';
import { formatCurrency } from '@utils/formater';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import DataLoadingWrapper from '../../common/DataLoadingWrapper';
import NewPaymentModalSkeleton from '../../common/Skeleton/NewPaymentModalSkeleton';
import FormCard from './components/FormCard';
import PAYMENT_STEPS from './constants';
import usePaymentController from './hooks/usePaymentController';
import Amount from './steps/Amount';
import Bank from './steps/Bank';
import Details from './steps/Details';
import Error from './steps/Error';
import Method from './steps/Method';
import Schedule from './steps/Schedule';
import SelectClient from './steps/SelectClient';
import Success from './steps/Success';

interface PaymentModalProps {
  onClose: () => void;
  open: boolean;
  selectedClientEmail?: string | null;
}

const PaymentModal = ({
  open,
  selectedClientEmail = null,
  onClose,
}: PaymentModalProps) => {
  const { t } = useTranslation();
  const {
    steps,
    paymentInProcess,
    formStep,
    formData,
    wooMoment,
    responseData,
    loadingPreselectedClient,
    prevFormStep,
    nextFormStep,
    createTransfer,
    goToFormStep,
    closeAndClearContext,
    clearAndNewPayment,
  } = usePaymentController({
    open,
    selectedClientEmail,
    onClose,
  });

  const minSavingsToShow = 5;

  const { currency, clientCurrency, reverse } = formData;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { savedAmount } = responseData || {};

  const WooMomentProps = useMemo(
    () =>
      savedAmount !== undefined && savedAmount > minSavingsToShow
        ? {
            title: (
              <>
                <Typography variant="body2" className="!font-light">
                  {t(`wooSuccess`)}
                </Typography>
                &nbsp;
                <Typography variant="body2">
                  <b>
                    {t(`wooSuccessAmount`, {
                      amount: formatCurrency(savedAmount ?? 0),
                      currency: reverse ? clientCurrency : currency,
                    })}
                  </b>
                </Typography>
              </>
            ),
            momentType: 'success' as const,
            icon: <SavingsOutlinedIcon />,
            onClose: closeAndClearContext,
          }
        : undefined,
    [savedAmount, clientCurrency, currency, reverse, t, closeAndClearContext]
  );

  return (
    <Dialog open={open} onClose={closeAndClearContext} fullWidth maxWidth="sm">
      {wooMoment && WooMomentProps ? (
        <WooMoments {...WooMomentProps} />
      ) : (
        <DialogTitle showCloseButton onClick={closeAndClearContext} />
      )}
      <DialogContent>
        <FormCard
          currentStep={formStep}
          activeStep={formStep - 1}
          steps={steps}
        >
          <DataLoadingWrapper
            customLoader={<NewPaymentModalSkeleton />}
            loadingData={paymentInProcess}
          >
            <>
              {loadingPreselectedClient &&
                selectedClientEmail &&
                formStep === PAYMENT_STEPS.SELECT_CLIENT && (
                  <Skeleton variant="rectangular" height={400} />
                )}
              {!loadingPreselectedClient &&
                formStep === PAYMENT_STEPS.SELECT_CLIENT && (
                  <SelectClient nextFormStep={nextFormStep} />
                )}
              {formStep === PAYMENT_STEPS.SELECT_AMOUNT && (
                <Amount
                  nextFormStep={nextFormStep}
                  prevFormStep={prevFormStep}
                />
              )}
              {formStep === PAYMENT_STEPS.PAYMENT_DETAIL && (
                <Details
                  nextFormStep={nextFormStep}
                  prevFormStep={prevFormStep}
                />
              )}
              {formStep === PAYMENT_STEPS.PAYMENT_METHOD && (
                <Method
                  nextFormStep={nextFormStep}
                  prevFormStep={prevFormStep}
                />
              )}
              {formStep === PAYMENT_STEPS.BANK_DEPOSIT && (
                <Bank
                  nextFormStep={createTransfer}
                  prevFormStep={goToFormStep}
                />
              )}
              {formStep === PAYMENT_STEPS.SCHEDULE_PAYMENT && (
                <Schedule
                  nextFormStep={createTransfer}
                  prevFormStep={goToFormStep}
                />
              )}
              {formStep === PAYMENT_STEPS.SUCCESS && (
                <Success
                  close={closeAndClearContext}
                  newPayment={clearAndNewPayment}
                />
              )}
              {formStep === PAYMENT_STEPS.ERROR && (
                <Error close={closeAndClearContext} retry={createTransfer} />
              )}
              {formStep === PAYMENT_STEPS.NOT_PROCESSED && (
                <Error
                  close={closeAndClearContext}
                  retry={createTransfer}
                  isNotProcessed
                />
              )}
              {formStep === PAYMENT_STEPS.PENDING_OF_TRANSFER && (
                <Success
                  close={closeAndClearContext}
                  newPayment={clearAndNewPayment}
                  isSchedule
                />
              )}
            </>
          </DataLoadingWrapper>
        </FormCard>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentModal;
